import React, { useEffect, useState } from "react";
import { ImageIcon, ShieldCheck, Upload } from "lucide-react";
import { toast } from "sonner";
import { wpApi, MAX_LOGO, LOGO_EXT, btnGhost, useWP } from "./wpApi";

// Server-geprueftes Logo: nur als <img> (Blob), nie inline als SVG-Markup.
export function WPLogoPreview({ orderId, logo }) {
  const { t } = useWP();
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!logo?.sha256) { setUrl(null); return undefined; }
    let alive = true;
    let obj = null;
    wpApi.logoBlob(orderId).then((r) => {
      if (!alive) return;
      obj = URL.createObjectURL(new Blob([r.data], { type: logo.mime }));
      setUrl(obj);
    }).catch(() => alive && setUrl(null));
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [orderId, logo?.sha256, logo?.mime]);

  if (!logo) return <div data-testid="wp-logo-empty" className="text-sm text-gray-500">{t("Noch kein Logo hinterlegt.")}</div>;
  return (
    <div className="flex items-center gap-4 min-w-0" data-testid="wp-logo-card">
      <div className="w-28 h-20 shrink-0 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center overflow-hidden p-2">
        {url ? <img src={url} alt={t("Logo")} data-testid="wp-logo-preview" className="max-w-full max-h-full object-contain" />
          : <ImageIcon className="w-6 h-6 text-gray-600" />}
      </div>
      <div className="text-xs text-gray-400 space-y-0.5 min-w-0">
        <div className="text-sm text-white truncate" data-testid="wp-logo-filename">{logo.filename}</div>
        <div>{logo.kind.toUpperCase()} · {(logo.size / 1024).toFixed(1)} KB{logo.width ? ` · ${logo.width}×${logo.height} px` : ""}</div>
        <div className="flex items-center gap-1 text-[#A3E635]"><ShieldCheck className="w-3.5 h-3.5" /> {t("Serverseitig geprüft")}</div>
        <div className="font-mono text-[10px] text-gray-500 truncate">SHA-256 {logo.sha256.slice(0, 16)}…</div>
      </div>
    </div>
  );
}

export function WPLogoPicker({ file, onChange, testid = "wp-logo-input" }) {
  const { t } = useWP();
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!file || file.name.toLowerCase().endsWith(".svg")) { setPreview(null); return undefined; }
    const u = URL.createObjectURL(file);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!LOGO_EXT.includes(f.name.split(".").pop().toLowerCase())) { toast.error(t("Erlaubt sind PNG, JPG, WebP und SVG")); return; }
    if (f.size > MAX_LOGO) { toast.error(t("Logo ist grösser als 2 MB")); return; }
    onChange(f);
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <label className={`${btnGhost} cursor-pointer focus-within:ring-2 focus-within:ring-[#A3E635]`} data-testid={`${testid}-label`}>
        <Upload className="w-4 h-4" /> {t(file ? "Andere Datei wählen" : "Logo wählen")}
        <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="sr-only" onChange={pick} data-testid={testid} />
      </label>
      {file && (
        <div className="flex items-center gap-2 text-xs text-gray-400 min-w-0" data-testid={`${testid}-selected`}>
          {preview ? <img src={preview} alt="" className="h-10 w-16 object-contain rounded bg-white/[0.04]" />
            : <span className="px-2 py-1 rounded bg-white/5">{t("SVG – Vorschau erst nach Serverprüfung")}</span>}
          <span className="truncate max-w-[160px]">{file.name}</span>
          <button type="button" onClick={() => onChange(null)} className="text-gray-500 hover:text-red-300 transition-colors"
            data-testid={`${testid}-clear`}>{t("Entfernen")}</button>
        </div>
      )}
      <span className="text-[11px] text-gray-500 w-full">{t("PNG, JPG, WebP oder SVG · höchstens 2 MB · wird serverseitig geprüft")}</span>
    </div>
  );
}
